<script setup lang="ts">
import HeaderTop from '~/components/layout/HeaderTop.vue';

const route = useRoute();
const to = route.query.to as string;
const params = { to: to };


defineOptions({
  name: "AccountLayout",
});

useAuthGuardRedirection(params);

// Navigation for Account page
const { loadNavigationElements } = useNavigation();
const { logout } = useUser();
const router = useRouter();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const { data } = await useAsyncData("mainNavigation", () => {
  return loadNavigationElements({ depth: 2 });
});
provide("swNavigation-main-navigation", data);

const { loadNavigationElements: loadFooterNavigationElements } = useNavigation({
  type: "footer-navigation",
});
const { data: footerData } = await useAsyncData("mainFooterNavigation", () => {
  return loadFooterNavigationElements({ depth: 2 });
});

async function invokeLogout() {
  logout();
  router.push("/");
}

const { user } = useUser();

provide("swNavigation-footer-navigation", footerData);

const miniCartModal = useMiniCartModal();
</script>

<template>
  <main>
    <HeaderTop />
    <LayoutHeader />
    <CheckoutSideCart :controller="miniCartModal" />
    <div class="account-layout">
      <LayoutNotifications />
      <LayoutBreadcrumbs />
      <div class="user-account px-4 mt-8">
        <div class="container mx-auto relative pb-12">
          <div class="div-wrap flex flex-wrap md:flex-nowrap items-start">
            <aside class="left w-full md:w-[183px]" aria-label="Sidebar">
              <div class="user-name pb-4 border-b border-b-custom-grey">
                <span class="font-bold text-base lg:text-xl">
                  {{ $t("account.hi") }}, {{ user?.firstName }}
                </span>
              </div>
              <div class="control-pnl py-4 border-b border-b-custom-grey">
                <ul
                  class="space-y-2 md:space-y-4 font-normal text-dark-bg transition duration-300"
                >
                  <li>
                    <NuxtLink
                      :to="formatLink(`/account`)"
                      class="hover:text-red-bg transition duration-300"
                      :class="{
                        'text-red-bg active-bg font-bold': $route.path === '/account',
                      }"
                    >
                      {{ $t("account.menu.accountOverviewHeader") }}
                    </NuxtLink>
                  </li>
                  <li>
                    <NuxtLink
                      :to="formatLink(`/account/profile`)"
                      class="hover:text-red-bg transition duration-300"
                      :class="{
                        'text-red-bg active-bg font-bold':
                          $route.path === '/account/profile',
                      }"
                    >
                      {{ $t("account.yourProfile") }}
                    </NuxtLink>
                  </li>
                  <li>
                    <NuxtLink
                      :to="formatLink(`/account/address`)"
                      class="hover:text-red-bg transition duration-300"
                      :class="{
                        'text-red-bg active-bg font-bold':
                          $route.path === '/account/address',
                      }"
                    >
                      {{ $t("account.yourAddress") }}
                    </NuxtLink>
                  </li>
                  <li>
                    <NuxtLink
                      :to="formatLink(`/account/payment`)"
                      class="hover:text-red-bg transition duration-300"
                      :class="{
                        'text-red-bg active-bg font-bold':
                          $route.path === '/account/payment',
                      }"
                    >
                      {{ $t("account.myPaymentsHeader") }}
                    </NuxtLink>
                  </li>
                  <li>
                    <NuxtLink
                      :to="formatLink(`/account/order`)"
                      class="hover:text-red-bg transition duration-300"
                      :class="{
                        'text-red-bg active-bg font-bold': $route.path === '/account/order',
                      }"
                    >
                      {{ $t("account.orderHistoryHeader") }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
              <div class="logout py-5">
                <button
                  class="font-bold text-base p-1 hover:text-red-bg transition duration-300 bg-transparent border-none"
                  @click="invokeLogout()"
                >
                  {{ $t("account.logout") }}
                </button>
              </div>
            </aside>
            <main
              class="right w-full md:w-[calc(100%-190px)] lg:w-[794px] md:ml-12 lg:ml-[122px]"
            >
              <slot />
            </main>
          </div>
        </div>
      </div>
      <LayoutFooter />
    </div>
  </main>
</template>
